<template>
  <BaseTableBlock class="px-5 py-1">
    <template v-slot:row-1>
      <div>
        <BaseButton
          @click="handleClick"
          :class="['px-5', rsCollection?.config.theme ? '!font-bold' : '']"
          :type="
            rsCollection?.config.theme
              ? `text--${rsCollection?.config.theme}`
              : 'text'
          "
          :label="
            searchTerm ||
            searchTag ||
            exploreTag ||
            overViewTag ||
            lastExploreTab
              ? $translate('Back to results', language, rsCollection?.locales)
              : $translate('Back to document', language, rsCollection?.locales)
          "
          :icon="'chevron-left'"
        ></BaseButton>
      </div>

      <div>
        <h4 class="mb-0">
          <div
            @click="backToDoc"
            class="inline cursor-pointer hover:font-bold transition-all duration-300 ease-in-out"
          >
            {{ info?.title }}
          </div>
          -
          {{ $translate('Page', language, rsCollection?.locales) }}
          {{ pageNum }}
          <label for="edit-metadata" @click="openMetadataDrawer()">
            <i
              class="mdi mdi-information text-xl text-secondaryG-700 px-3 cursor-pointer"
            ></i>
          </label>
        </h4>
      </div>
      <!-- TODO: change backend to fetch search results based on doc or page
       to support search results pagination -->
      <!-- v-if="!searchTerm" -->
      <div>
        <BasePagination
          style="margin-top: 0rem !important"
          :siteTheme="rsCollection?.config.theme"
          :total="totalPages"
          :current="isNaN(parseInt(pageNum)) ? '' : parseInt(pageNum)"
          @changePage="$emit('changePage', $event)"
          :showPerPage="false"
          :withAnimation="false"
        ></BasePagination>
      </div>
    </template>
  </BaseTableBlock>
</template>

<script>
import { mapState } from 'pinia'
import { useSiteStore } from '~/stores/sites'
import DrawerMetadata from '~/components/Drawer/Metadata.vue'

const mapper = createMapper('browser')

export default {
  setup() {
    const sitesApi = useSites()
    const sitesStore = useSiteStore()
    return {
      sitesApi,
      sitesStore,
    }
  },
  data() {
    return {
      testPageFetch: null,
    }
  },
  props: {
    info: {
      type: Object,
      default: () => {},
    },
    rsCollection: {
      type: Object,
      default: () => ({}),
    },
    currentPageId: {
      type: Number,
      default: null,
    },
    lastExploreTab: {
      type: String,
      default: null,
    },
  },
  computed: {
    ...mapState(useSiteStore, ['language']),
    collId() {
      return this.rsCollection.colId
    },
    isReady() {
      return this.info !== null
    },
    pageNum() {
      return this.currentPageId
    },
    searchTerm() {
      return mapper.fromURL(this.$route.query).term || ''
    },
    searchTag() {
      return this.$route.query.type
    },
    exploreTag() {
      return this.$route.query.explore
    },
    overViewTag() {
      return this.$route.query.overview
    },
    totalPages() {
      return this.info?.maxPage
    },
    url() {
      return this.$route.params.url
    },

    levels() {
      const home = {
        to: { path: '/sites/' + this.collId },
        label: 'Home',
      }
      if (!this.isReady) {
        return [home]
      } else {
        const levels = [
          home,
          {
            to: {
              path: '/sites/' + this.collId + '/browse',
            },
            label: this.$t('Browse'),
          },
          {
            to: {
              path: `/sites/${this.collId}/doc/${this.info.docId}`,
            },
            label: this.info.title,
          },
        ]
        return levels
      }
    },
  },

  methods: {
    handleEnableEditing() {
      window
        .open(
          this.localePath(
            '/collection/' +
              this.rsCollection?.colId +
              '/doc/' +
              this.$route.params.docid +
              '/detail/' +
              this.$route.params.pagenum
          ),
          '_blank'
        )
        .focus()
    },
    handleClick() {
      if (this.lastExploreTab === 'Hierarchy') {
        this.backToHierarchy()
      } else if (this.overViewTag) {
        this.backToDocOverview()
      } else if (this.exploreTag) {
        this.backToDocSearch()
      } else if (this.searchTerm) {
        this.backToSearch()
      } else if (this.searchTag) {
        this.backToExplore()
      } else {
        this.backToDoc()
      }
    },
    backToDoc() {
      this.$router.push({ path: `/sites/${this.url}/doc/${this.info.docId}` })
    },
    backToSearch() {
      this.$router.push({
        path: `/sites/${this.url}/search/`,
        query: {
          term: this.searchTerm,
          f: 1,
        },
      })
    },
    backToExplore() {
      this.$router.push({
        path: `/sites/${this.url}/tags/`,
        query: {
          type: this.searchTag,
        },
      })
    },
    backToHierarchy() {
      this.$router.push({
        path: `/sites/${this.url}/browse`,
        query: {
          term: this.searchTerm,
          lastExploreTab: 'Hierarchy',
        },
      })
    },
    backToDocOverview() {
      this.$router.push({
        path: `/sites/${this.url}/browse`,
        query: {
          term: this.searchTerm,
          colId: this.overViewTag,
        },
      })
    },
    backToDocSearch() {
      this.$router.push({
        path: `/sites/${this.url}/doc/${this.info.docId}`,
        query: {
          term: this.searchTerm,
          f_title: this.sitesStore.filters?.f_title,
        },
      })
    },
    viewPageInDoc() {
      const rd = this.$router.resolve({
        path: `/sites/${this.url}/doc/${this.info.docId}/detail`,
        query: {
          pageid: this.currentPageId,
        },
      })
      window.open(rd.href, '_blank')
    },
    handlePager(value) {
      this.$router.push({
        params: {
          ...this.$route.params,
        },
        query: {
          ...this.$route.query,
          pageid: value,
        },
      })
    },
    truncate(str, n) {
      return str.length > n ? str.substr(0, n - 1) + '...' : str
    },
    async openMetadataDrawer() {
      const slideOver = {
        slideOver: 'edit-metadata',
        slideContent: {
          component: DrawerMetadata,
          data: {},
          props: {
            collId: this.rsCollection.colId,
            docId: Number(this.$route.params.docid),
            url: this.rsCollection.url,
            pageNr: this.currentPageId,
            isSitesPage: true,
          },
        },
      }
      this.$bus.emit('slideOver', slideOver)
    },
  },
}
</script>

<style>
.level.is-fullwidth {
  width: 100%;
}

.has-shadow {
  box-shadow: 0 2px 0 0 whitesmoke;
}
</style>
